import { FC, useMemo } from "react";
import {
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@capacit-com/lara-law-shared-ui";
import { FavoriteSearchesWidgetConfig } from "@/models/dashboard/WidgetConfig";
import useSearch from "@/hooks/useSearch";
import useUsers from "@/hooks/useUsers";
import { dedupeSupersededTagSearches } from "../lib/dedupeSupersededTagSearches";
import { parseOptionalMaxItems } from "../lib/parseWidgetMaxItems";
import {
  buildFullDisplayOrderForGlobalUpdate,
  resolveItemOrder,
  usesWidgetItemOrder,
} from "../lib/resolveItemOrder";
import { formatSearchDisplayLabel } from "../lib/formatSearchDisplayLabel";
import { getTabsName } from "@/pages/global-search/utils/searchUtils";
import SearchDisplayLabel from "../components/SearchDisplayLabel";
import UserDataOrderSection from "./UserDataOrderSection";
import { Search } from "@/models/search/Search";

interface FavoriteSearchesConfigFormProps {
  config: FavoriteSearchesWidgetConfig;
  onChange: (config: FavoriteSearchesWidgetConfig) => void;
}

const FavoriteSearchesConfigForm: FC<FavoriteSearchesConfigFormProps> = ({
  config,
  onChange,
}) => {
  const { useMyFavoriteSearches, transformStoredSearch } = useSearch();
  const { reorderFavoriteSearches } = useUsers();
  const favoritesQuery = useMyFavoriteSearches();

  const useGlobalOrder = !usesWidgetItemOrder(
    config.useGlobalOrder,
    config.itemOrder
  );
  const layout = config.layout ?? "grid";

  const { displayItems, searchById } = useMemo(() => {
    if (!favoritesQuery.data) {
      return { displayItems: [], searchById: new Map<string, Search>() };
    }

    const searches = dedupeSupersededTagSearches(
      favoritesQuery.data.searches.map(transformStoredSearch)
    );
    const items = searches.map((search, index) => ({
      id:
        search.id ??
        search.favoriteLinkId ??
        `${search.searchTerm}-${search.searchedAt ?? "no-date"}-${index}`,
      search,
    }));

    const ordered = resolveItemOrder(items, {
      globalOrder: favoritesQuery.data.displayOrder,
      itemOrder: config.itemOrder,
      useGlobalOrder: config.useGlobalOrder,
    });

    const byId = new Map<string, Search>();
    const list = ordered.map((item) => {
      byId.set(item.id, item.search);
      return {
        id: item.id,
        label: formatSearchDisplayLabel(item.search),
        sublabel: getTabsName(item.search.tab),
      };
    });

    return { displayItems: list, searchById: byId };
  }, [
    favoritesQuery.data,
    config.itemOrder,
    config.useGlobalOrder,
    transformStoredSearch,
  ]);

  const allFavoriteIds = useMemo(() => {
    if (!favoritesQuery.data) {
      return [];
    }

    const searches = favoritesQuery.data.searches.map(transformStoredSearch);
    return searches.map(
      (search, index) =>
        search.id ??
        search.favoriteLinkId ??
        `${search.searchTerm}-${search.searchedAt ?? "no-date"}-${index}`
    );
  }, [favoritesQuery.data, transformStoredSearch]);

  const currentOrderedIds = useMemo(() => {
    if (!useGlobalOrder && config.itemOrder && config.itemOrder.length > 0) {
      return config.itemOrder;
    }
    return displayItems.map((item) => item.id);
  }, [useGlobalOrder, config.itemOrder, displayItems]);

  const updateConfig = (updates: Partial<FavoriteSearchesWidgetConfig>) => {
    onChange({ ...config, ...updates });
  };

  const handleUseGlobalOrderChange = (useGlobal: boolean) => {
    if (useGlobal) {
      updateConfig({ useGlobalOrder: true, itemOrder: undefined });
    } else {
      updateConfig({
        useGlobalOrder: false,
        itemOrder: currentOrderedIds,
      });
    }
  };

  const handleItemOrderChange = (orderedIds: string[]) => {
    updateConfig({ useGlobalOrder: false, itemOrder: orderedIds });
  };

  const handleUpdateGlobalOrder = async () => {
    if (allFavoriteIds.length === 0) {
      return;
    }

    const idsForApi = buildFullDisplayOrderForGlobalUpdate(
      currentOrderedIds,
      allFavoriteIds,
      favoritesQuery.data?.displayOrder
    );
    await reorderFavoriteSearches.mutateAsync(idsForApi);
    updateConfig({ useGlobalOrder: true, itemOrder: undefined });
  };

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label>Layout</Label>
        <Select
          value={layout}
          onValueChange={(value) =>
            updateConfig({ layout: value as "grid" | "list" })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="grid">Gitter (2 kolonner)</SelectItem>
            <SelectItem value="list">Liste (1 kolonne)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="favorite-searches-max-items">
          Maks. antal (valgfrit)
        </Label>
        <Input
          id="favorite-searches-max-items"
          type="number"
          min={1}
          placeholder="Vis alle"
          value={config.maxItems ?? ""}
          onChange={(e) => {
            updateConfig({ maxItems: parseOptionalMaxItems(e.target.value) });
          }}
        />
      </div>

      <UserDataOrderSection
        useGlobalOrder={useGlobalOrder}
        items={displayItems}
        droppableId="favorite-searches-config-order"
        onUseGlobalOrderChange={handleUseGlobalOrderChange}
        onItemOrderChange={handleItemOrderChange}
        onUpdateGlobalOrder={handleUpdateGlobalOrder}
        isUpdatingGlobal={reorderFavoriteSearches.isPending}
        renderItemLabel={(item) => {
          const search = searchById.get(item.id);
          return search ? (
            <SearchDisplayLabel search={search} />
          ) : (
            <span className="truncate text-sm">{item.label}</span>
          );
        }}
      />
    </div>
  );
};

export default FavoriteSearchesConfigForm;
